import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, AlertCircle, Clock, Mail, RefreshCw, ArrowRight, MessageCircle, Sparkles, Check } from 'lucide-react';
import LanguageSelector from '../components/LanguageSelector';
import useTranslation from '../data/useTranslation';
import { useCurrency } from '../contexts/CurrencyContext';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function ThankYou() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const isUpgrade = searchParams.get('upgrade') === '1';
  const { t } = useTranslation();
  const { format } = useCurrency();
  
  const [status, setStatus] = useState('verifying');
  const [orderDetails, setOrderDetails] = useState(null);
  const [resendCount, setResendCount] = useState(0);
  const [resending, setResending] = useState(false);
  
  useEffect(() => {
    const verifyPayment = async () => {
      if (!sessionId) { setStatus('error'); return; }
      try {
        const response = await fetch(`${API}/api/checkout/verify?session_id=${sessionId}`);
        const data = await response.json();
        if (data.status === 'paid') {
          setStatus('success');
          setOrderDetails(data);
          // PostHog: track upgrade completion
          if (isUpgrade) {
            try {
              if (window.posthog && typeof window.posthog.capture === 'function') {
                window.posthog.capture('upgrade_completed', {
                  to_pack: data.pack || null,
                  session_id: sessionId,
                });
              }
            } catch (e) { /* ignore */ }
          }
        } else if (data.status === 'pending') {
          setStatus('pending');
        } else {
          setStatus('error');
        }
      } catch (err) {
        setStatus('error');
      }
    };
    verifyPayment();
  }, [sessionId, isUpgrade]);
  
  const handleResendEmail = async () => {
    if (resendCount >= 3) return;
    setResending(true);
    try {
      await fetch(`${API}/api/checkout/resend-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId })
      });
      setResendCount(prev => prev + 1);
    } catch (err) {}
    finally { setResending(false); }
  };
  
  if (status === 'verifying') {
    return (
      <div className="min-h-screen bg-[#111827] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">{t.thankYou.verifying}</p>
        </div>
      </div>
    );
  }
  
  if (status === 'error') {
    return (
      <div className="min-h-screen bg-[#111827] flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="flex justify-end mb-4"><LanguageSelector /></div>
          <div className="bg-[#1F2937] rounded-2xl p-8 border border-gray-700">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-4">{t.thankYou.errorTitle}</h2>
            <p className="text-gray-400 mb-6">{t.thankYou.errorDesc}</p>
            <a href="mailto:contact@bpm.com" className="inline-flex items-center gap-2 px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg transition-colors">
              <MessageCircle className="w-5 h-5" />
              {t.thankYou.contactUs}
            </a>
          </div>
          <Link to="/" className="inline-block mt-6 text-gray-500 hover:text-gray-300 text-sm">← {t.common.backToHome}</Link>
        </div>
      </div>
    );
  }
  
  if (status === 'pending') {
    return (
      <div className="min-h-screen bg-[#111827] flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="flex justify-end mb-4"><LanguageSelector /></div>
          <div className="bg-[#1F2937] rounded-2xl p-8 border border-gray-700">
            <Clock className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-4">{t.thankYou.pendingTitle}</h2>
            <p className="text-gray-400 mb-6">{t.thankYou.pendingDesc}</p>
          </div>
        </div>
      </div>
    );
  }
  
  // Upgrade success view (?upgrade=1)
  if (isUpgrade) {
    const packName = orderDetails?.pack === 'duo'
      ? t.landing.duoName
      : (orderDetails?.pack === 'complete' ? t.landing.completeName : '');
    return (
      <div className="min-h-screen bg-[#111827] flex items-center justify-center px-4 py-8">
        <div className="max-w-md w-full">
          <div className="flex justify-end mb-4"><LanguageSelector /></div>
          <div
            className="bg-gradient-to-br from-orange-500/10 to-purple-500/10 border border-orange-500/40 rounded-2xl p-8 text-center"
            data-testid="thankyou-upgrade"
          >
            <div className="w-20 h-20 bg-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Sparkles className="w-12 h-12 text-orange-400" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3" data-testid="thankyou-upgrade-title">
              {t.thankYou.upgradeTitle.replace('{packName}', packName)}
            </h2>
            <p className="text-gray-300 mb-8">{t.thankYou.upgradeBody}</p>
            <Link
              to="/dashboard"
              data-testid="thankyou-upgrade-cta"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-xl transition-colors"
            >
              {t.thankYou.upgradeCta}
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#111827] flex items-center justify-center px-4 py-8">
      <div className="max-w-md w-full">
        <div className="flex justify-end mb-4"><LanguageSelector /></div>

        {/* Upsell for Discovery pack only (Phase 3.7) */}
        {/* TODO: Backend doit gérer l'upgrade en proratisant 9.99€ déjà payés */}
        {orderDetails?.pack === 'decouverte' && (
          <div
            className="mb-6 rounded-2xl bg-gradient-to-br from-orange-500/15 to-purple-500/15 border border-orange-500/40 p-6 shadow-xl"
            data-testid="thankyou-upsell"
          >
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5 text-orange-400" />
              <h3 className="text-lg font-bold text-white" data-testid="thankyou-upsell-title">
                {t.thankYou.upsellTitlePrefix} {format(20)} {t.thankYou.upsellTitleSuffix}
              </h3>
            </div>
            <p className="text-gray-300 text-sm mb-4">{t.thankYou.upsellSub}</p>
            <ul className="space-y-2 mb-5">
              {[t.thankYou.upsellFeat1, t.thankYou.upsellFeat2, t.thankYou.upsellFeat3, t.thankYou.upsellFeat4].map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-200">
                  <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/checkout?pack=complete&upgrade=1"
              data-testid="thankyou-upsell-btn"
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-xl transition-colors"
            >
              {t.thankYou.upsellBtn}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}

        <div className="bg-[#1F2937] rounded-2xl p-8 border border-gray-700 text-center">
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-green-500" />
          </div>
          
          <h2 className="text-3xl font-bold text-white mb-2" data-testid="thankyou-title">{t.thankYou.successTitle}</h2>
          
          {orderDetails?.pack && (
            <p className="text-orange-400 font-medium mb-6">
              {orderDetails.pack === 'decouverte' ? t.landing.discoveryName : orderDetails.pack === 'duo' ? t.landing.duoName : t.landing.completeName}
            </p>
          )}
          
          <div className="bg-[#111827] rounded-xl p-6 mb-6">
            <Mail className="w-10 h-10 text-orange-500 mx-auto mb-3" />
            <h3 className="text-white font-semibold mb-2">{t.thankYou.checkEmail}</h3>
            <p className="text-gray-400 text-sm">{t.thankYou.emailComing}</p>
            
            <button onClick={handleResendEmail} disabled={resending || resendCount >= 3}
              className="mt-4 px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 rounded-lg text-sm text-gray-300 transition-colors flex items-center gap-2 mx-auto">
              <RefreshCw className={`w-4 h-4 ${resending ? 'animate-spin' : ''}`} />
              {resending ? t.thankYou.resending : t.thankYou.resend}
            </button>
            {resendCount > 0 && (
              <p className="text-xs text-gray-500 mt-2">{3 - resendCount} {t.thankYou.resendsLeft}</p>
            )}
          </div>
          
          <div className="space-y-3 text-sm text-gray-400">
            <p>{t.thankYou.tipSpam}</p>
            <p>{t.thankYou.tipFrom} <span className="text-white">noreply@bpm.com</span></p>
          </div>
          
          <div className="mt-8 pt-6 border-t border-gray-700">
            <p className="text-gray-500 text-sm">{t.thankYou.needHelp}</p>
            <a href="mailto:contact@bpm.com" className="text-orange-500 hover:underline text-sm">{t.thankYou.contactUs}</a>
          </div>
        </div>
        
        <div className="mt-6 text-center">
          <Link to="/" className="text-gray-500 hover:text-gray-300 text-sm">← {t.common.backToHome}</Link>
        </div>
      </div>
    </div>
  );
}
